import { useEffect, useRef, useState } from 'react';
import { useLocale } from '../../state/useLocale';
import { efficiencyHeroCopy } from '../../lib/strings/efficiency-hero';

/** The address the bar's and the journey's start buttons point at. */
export const AGENT_LINE_ID = 'agent-line';

/**
 * "Give this to your agent": the one line a visitor hands their own agent
 * (docs/server-workspace.md, 6). The agent reads /start.md, which tells it
 * how to connect and nothing about any task. Copying is the only thing this
 * does; nothing downloads and nothing opens.
 *
 * Where the clipboard is refused (an insecure origin, an old browser), the
 * line is selected instead so a long-press or Ctrl+C still takes it.
 */
export function AgentLine() {
  const { locale } = useLocale();
  const s = efficiencyHeroCopy[locale];
  const [copied, setCopied] = useState<'no' | 'yes' | 'failed'>('no');
  const code = useRef<HTMLElement>(null);
  useEffect(() => {
    if (copied !== 'yes') return;
    const t = window.setTimeout(() => setCopied('no'), 2500);
    return () => window.clearTimeout(t);
  }, [copied]);
  const select = () => {
    const node = code.current;
    const range = node ? document.createRange() : null;
    if (!node || !range) return;
    range.selectNodeContents(node);
    window.getSelection()?.removeAllRanges();
    window.getSelection()?.addRange(range);
  };
  const copy = () => {
    const done = navigator.clipboard?.writeText(s.line);
    if (!done) { select(); setCopied('failed'); return; }
    done.then(() => setCopied('yes'), () => { select(); setCopied('failed'); });
  };
  return (
    <div className="agent-line" id={AGENT_LINE_ID} tabIndex={-1}>
      <p className="label">{s.howTitle}</p>
      <p className="note agent-line__for">{s.lineLabel}</p>
      <div className="agent-line__box">
        <code ref={code}>{s.line}</code>
        <button type="button" className="btn btn-solid" onClick={copy}>
          {copied === 'yes' ? s.copied : s.copy}
        </button>
      </div>
      <p className="note agent-line__status" role="status">{copied === 'failed' ? s.copyFailed : ''}</p>
      <p className="agent-line__next">{s.next}</p>
    </div>
  );
}
